import React, { useEffect, useState } from 'react'; // eslint-disable-line

import { Button, Grid, Tooltip, withStyles, Typography } from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import { Field, FormSpy } from 'react-final-form';
import {
    FullWidthRadioGroup,
    RadioOption,
    FullWidthTextField,
    Condition,
    True,
    When,
    WhenFieldChanges,
    VirtualizedSearchSelectField,
} from '../../../../../forms';
import {
    requiredValidator,
    maximumLength,
    composeValidators,
    conditionalRsaIdNumberValidator,
} from '../../../../../forms/validations';
import styles, { StyledComponent } from '../../../../../styles/styles';
import { Dependant } from '../reducer';
import { InitialsMask, NumberMask } from '../../../../../forms/input-masks';
import { getValueFromObject } from '../../../../../shared-functions';
import { CodeList, UniversalBranch } from '../../../../../lookups';
import { LoadBankBranches, loadBankBranchesAction } from '../../../../../lookups/bank-branch-codes';
import { State } from '../../../../../redux/root-reducer';
import { connect } from 'react-redux';

interface BankingProps extends StyledComponent {
    index: number;
    deleteEntry: (index: number) => void;
    readonly bankNames: CodeList<string>;
    readonly loadBankBranches: LoadBankBranches;
    readonly universalBranches: UniversalBranch[];
}
const mapStateToProps = (state: State) => ({
    bankNames: state.claimSession.lookups.bankNames,
    universalBranches: state.claimSession.lookups.universalBranches,
});

const mapDispatchToProps = {
    loadBankBranches: loadBankBranchesAction,
};
const defaultBranches: UniversalBranch[] = [];
export const BankingDetailsViewComponent = connect(
    mapStateToProps,
    mapDispatchToProps
)(
    ({
        bankNames,
        universalBranches,
        loadBankBranches,
        index,
        deleteEntry,
        classes,
    }: BankingProps) => {
        const [branchCodes, setBranchCodes] = useState(defaultBranches);
        const [loadBranches, setLoadBranches] = useState(false);
        const [loadingBranches, setLoadingBranches] = useState(false);
        const getBranches = (bank: string) => {
            if (loadBranches && !loadingBranches && branchCodes) {
                setLoadingBranches(true);
                loadBankBranches(bank)
                    .then(result => {
                        setBranchCodes(result);
                    })
                    .finally(() => {
                        setLoadBranches(false);
                        setLoadingBranches(false);
                    });
            }
        };
        return FormSpy<Dependant>({
            // eslint-disable-next-line
        render: ({ form }) => {
                const deleteValues = (occurence: number) => {
                    // eslint-disable-next-line
                const bankingDetailsInformation = form.getState().values;
                    if (
                        bankingDetailsInformation !== undefined &&
                        bankingDetailsInformation.bankingDetails !== undefined
                    ) {
                        bankingDetailsInformation.bankingDetails.splice(occurence, 1);
                    }
                };
                return (
                    <React.Fragment>
                        <Grid item xs={12} container spacing={5}>
                            <Grid item xs={12} container>
                                <Grid item xs={11}>
                                    <Typography variant="subtitle2" className={classes.marginTop}>
                                        Banking details({index + 1})
                                    </Typography>
                                </Grid>
                                <Grid item xs={1} justify="flex-end">
                                    <Tooltip title="Delete">
                                        <Button
                                            id={`Delete_breakdown_${index}`}
                                            color="secondary"
                                            onClick={() => {
                                                deleteValues(index);
                                                deleteEntry(index);
                                            }}
                                        >
                                            <Delete />
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <FullWidthRadioGroup
                                    label="Additional Information"
                                    name={`bankingDetails[${index}].investmentIdType`}
                                    validate={requiredValidator}
                                >
                                    <RadioOption value="Individual" />
                                    <RadioOption value="CompulsoryAnnuity" />
                                </FullWidthRadioGroup>
                            </Grid>
                            <Condition
                                when={`bankingDetails[${index}]investmentIdType`}
                                matchesChild
                            >
                                <When is="">
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}].accountHoldersInitials`}
                                            validate={maximumLength(5)}
                                            mask={InitialsMask}
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'accountHoldersInitials'}
                                    />
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}].accountHoldersName`}
                                            label="Account Holder's surname/company name"
                                            validate={composeValidators(
                                                requiredValidator,
                                                maximumLength(60)
                                            )}
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'accountHoldersName'}
                                    />
                                    <Grid item xs={6}>
                                        <FullWidthRadioGroup
                                            label="Account holder's identity type"
                                            name={`bankingDetails[${index}].accountHoldersIdType`}
                                            validate={requiredValidator}
                                        >
                                            <RadioOption value="ID" />
                                            <RadioOption value="Passport" />
                                            <RadioOption
                                                value="Registration"
                                                label="Registration Number"
                                            />
                                        </FullWidthRadioGroup>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            label="Account holder's identity number"
                                            name={`bankingDetails[${index}].accountHoldersIdNumber`}
                                            validate={composeValidators(
                                                requiredValidator,
                                                conditionalRsaIdNumberValidator(
                                                    values =>
                                                        getValueFromObject(
                                                            values,
                                                            `bankingDetails`,
                                                            'accountHoldersIdType'
                                                        ) === 'ID'
                                                )
                                            )}
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'accountHoldersIdNumber'}
                                    />
                                    <Grid item xs={6}>
                                        <VirtualizedSearchSelectField
                                            name={`bankingDetails[${index}].bankName`}
                                            validate={requiredValidator}
                                            options={bankNames}
                                            matchPos="start"
                                        />
                                    </Grid>

                                    <ResetStatus prefix={`bankingDetails`} property={'bankName'} />
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}].accountNumber`}
                                            validate={requiredValidator}
                                            mask={NumberMask}
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'accountNumber'}
                                    />
                                    <Field
                                        name={`bankingDetails[${index}].bankName`}
                                        subscription={{ value: true }}
                                        render={({ input: { value: bankName } }) => {
                                            const [
                                                localBranchCodes,
                                                setLocalBranchCodes,
                                            ] = useState<UniversalBranch[]>([]);
                                            const [loading, setLoading] = useState(false);

                                            useEffect(() => {
                                                if (!bankName) {
                                                    setLocalBranchCodes([]);
                                                    return;
                                                }

                                                const universalBranch = universalBranches.find(
                                                    uc => uc.bankName === bankName
                                                );

                                                if (universalBranch) {
                                                    setLocalBranchCodes([universalBranch]);
                                                } else {
                                                    setLoading(true);
                                                    loadBankBranches(bankName)
                                                        .then(result => setLocalBranchCodes(result))
                                                        .finally(() => setLoading(false));
                                                }
                                            }, [bankName]);

                                            const universalBranch = universalBranches.find(
                                                uc => uc.bankName === bankName
                                            );

                                            return (
                                                <Grid item xs={6}>
                                                    <VirtualizedSearchSelectField
                                                        name={`bankingDetails[${index}].branchCode`}
                                                        options={localBranchCodes}
                                                        label="Branch Name"
                                                        validate={requiredValidator}
                                                        disabled={
                                                            !!universalBranch &&
                                                            localBranchCodes.length === 1
                                                        }
                                                        loading={loading}
                                                        matchPos="start"
                                                    />
                                                </Grid>
                                            );
                                        }}
                                    />
                                    <WhenFieldChanges
                                        field={`bankingDetails[${index}].bankName`}
                                        set={`bankingDetails[${index}].branchCode`}
                                        to={(bankName: string) => {
                                            const universalBranch = universalBranches.find(
                                                uc => uc.bankName === bankName
                                            );
                                            if (universalBranch) {
                                                return universalBranch.value;
                                            }
                                            setLoadBranches(true);
                                            return null;
                                        }}
                                    />
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`BankingDetails[${index}].branchCode`}
                                            validate={requiredValidator}
                                            mask={NumberMask}
                                            disabled
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'branchCode'}
                                    />
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}].specialInstructions`}
                                            label="Special Instructions"
                                            multiline
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FullWidthRadioGroup
                                            name={`bankingDetails[${index}].accountType`}
                                            validate={requiredValidator}
                                        >
                                            <RadioOption value="Current" />
                                            <RadioOption value="Savings" />
                                            <RadioOption value="Transmission" />
                                        </FullWidthRadioGroup>
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'accountType'}
                                    />

                                    <React.Fragment>
                                        <Grid item xs={6}>
                                            <FullWidthRadioGroup
                                                name={`bankingDetails[${index}].accountHolderRelationship`}
                                                validate={requiredValidator}
                                            >
                                                <RadioOption value="Own" />
                                                <RadioOption value="Joint" />
                                                <RadioOption value="Other" />
                                            </FullWidthRadioGroup>
                                        </Grid>
                                        <WhenFieldChanges
                                            field={`bankingDetails[${index}].accountHolderRelationship`}
                                            condition={value => value !== 'Other'}
                                            set={`bankingDetails[${index}].accountHolderRelationshipDescription`}
                                            to={null}
                                        />
                                        <Condition
                                            when={`bankingDetails[${index}].accountHolderRelationship`}
                                            is="Other"
                                        >
                                            <True>
                                                <Grid item xs={6}>
                                                    <FullWidthTextField
                                                        name={`bankingDetails[${index}].accountHolderRelationshipDescription`}
                                                        validate={requiredValidator}
                                                    />
                                                </Grid>
                                            </True>
                                        </Condition>
                                    </React.Fragment>

                                    <Grid item xs={6}>
                                        <FullWidthRadioGroup
                                            name={`bankingDetails[${index}].bankAccountStatus`}
                                        >
                                            <RadioOption value="Verified" disabled />
                                            <RadioOption value="Not verified" disabled />
                                        </FullWidthRadioGroup>
                                    </Grid>
                                </When>
                                <When is="Individual">
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}].accountHoldersInitials`}
                                            validate={maximumLength(5)}
                                            mask={InitialsMask}
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'accountHoldersInitials'}
                                    />
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}].accountHoldersName`}
                                            label="Account Holder's surname/company name"
                                            validate={composeValidators(
                                                requiredValidator,
                                                maximumLength(60)
                                            )}
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'accountHoldersName'}
                                    />
                                    <Grid item xs={6}>
                                        <FullWidthRadioGroup
                                            label="Account holder's identity type"
                                            name={`bankingDetails[${index}].accountHoldersIdType`}
                                            validate={requiredValidator}
                                        >
                                            <RadioOption value="ID" />
                                            <RadioOption value="Passport" />
                                            <RadioOption
                                                value="Registration"
                                                label="Registration Number"
                                            />
                                        </FullWidthRadioGroup>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            label="Account holder's identity number"
                                            name={`bankingDetails[${index}].accountHoldersIdNumber`}
                                            validate={composeValidators(
                                                requiredValidator,
                                                conditionalRsaIdNumberValidator(
                                                    values =>
                                                        getValueFromObject(
                                                            values,
                                                            `bankingDetails`,
                                                            'accountHoldersIdType'
                                                        ) === 'ID'
                                                )
                                            )}
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'accountHoldersIdNumber'}
                                    />
                                    <Grid item xs={6}>
                                        <VirtualizedSearchSelectField
                                            name={`bankingDetails[${index}].bankName`}
                                            validate={requiredValidator}
                                            options={bankNames}
                                            matchPos="start"
                                        />
                                    </Grid>

                                    <ResetStatus prefix={`bankingDetails`} property={'bankName'} />
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}].accountNumber`}
                                            validate={requiredValidator}
                                            mask={NumberMask}
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'accountNumber'}
                                    />
                                    <Field
                                        name={`bankingDetails[${index}].bankName`}
                                        subscription={{ value: true }}
                                        render={({ input: { value: bankName } }) => {
                                            const [
                                                localBranchCodes,
                                                setLocalBranchCodes,
                                            ] = useState<UniversalBranch[]>([]);
                                            const [loading, setLoading] = useState(false);

                                            useEffect(() => {
                                                if (!bankName) {
                                                    setLocalBranchCodes([]);
                                                    return;
                                                }

                                                const universalBranch = universalBranches.find(
                                                    uc => uc.bankName === bankName
                                                );

                                                if (universalBranch) {
                                                    setLocalBranchCodes([universalBranch]);
                                                } else {
                                                    setLoading(true);
                                                    loadBankBranches(bankName)
                                                        .then(result => setLocalBranchCodes(result))
                                                        .finally(() => setLoading(false));
                                                }
                                            }, [bankName]);

                                            const universalBranch = universalBranches.find(
                                                uc => uc.bankName === bankName
                                            );

                                            return (
                                                <Grid item xs={6}>
                                                    <VirtualizedSearchSelectField
                                                        name={`bankingDetails[${index}].branchCode`}
                                                        options={localBranchCodes}
                                                        label="Branch Name"
                                                        validate={requiredValidator}
                                                        disabled={
                                                            !!universalBranch &&
                                                            localBranchCodes.length === 1
                                                        }
                                                        loading={loading}
                                                        matchPos="start"
                                                    />
                                                </Grid>
                                            );
                                        }}
                                    />
                                    <WhenFieldChanges
                                        field={`bankingDetails[${index}].bankName`}
                                        set={`bankingDetails[${index}].branchCode`}
                                        to={(bankName: string) => {
                                            const universalBranch = universalBranches.find(
                                                uc => uc.bankName === bankName
                                            );
                                            if (universalBranch) {
                                                return universalBranch.value;
                                            }
                                            if (false) {
                                                getBranches('');
                                            }
                                            setLoadBranches(true);
                                            return null;
                                        }}
                                    />
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}].branchCode`}
                                            validate={requiredValidator}
                                            mask={NumberMask}
                                            disabled
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'branchCode'}
                                    />
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}].specialInstructions`}
                                            label="Special Instructions"
                                            multiline
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FullWidthRadioGroup
                                            name={`bankingDetails[${index}].accountType`}
                                            validate={requiredValidator}
                                        >
                                            <RadioOption value="Current" />
                                            <RadioOption value="Savings" />
                                            <RadioOption value="Transmission" />
                                        </FullWidthRadioGroup>
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'accountType'}
                                    />

                                    <React.Fragment>
                                        <Grid item xs={6}>
                                            <FullWidthRadioGroup
                                                name={`bankingDetails[${index}].accountHolderRelationship`}
                                                validate={requiredValidator}
                                            >
                                                <RadioOption value="Own" />
                                                <RadioOption value="Joint" />
                                                <RadioOption value="Other" />
                                            </FullWidthRadioGroup>
                                        </Grid>
                                        <WhenFieldChanges
                                            field={`bankingDetails[${index}].accountHolderRelationship`}
                                            condition={value => value !== 'Other'}
                                            set={`bankingDetails[${index}].accountHolderRelationshipDescription`}
                                            to={null}
                                        />
                                        <Condition
                                            when={`bankingDetails[${index}].accountHolderRelationship`}
                                            is="Other"
                                        >
                                            <True>
                                                <Grid item xs={6}>
                                                    <FullWidthTextField
                                                        name={`bankingDetails[${index}].accountHolderRelationshipDescription`}
                                                        validate={requiredValidator}
                                                    />
                                                </Grid>
                                            </True>
                                        </Condition>
                                    </React.Fragment>

                                    <Grid item xs={6}>
                                        <FullWidthRadioGroup
                                            name={`bankingDetails[${index}].bankAccountStatus`}
                                        >
                                            <RadioOption value="Verified" disabled />
                                            <RadioOption value="Not verified" disabled />
                                        </FullWidthRadioGroup>
                                    </Grid>
                                </When>
                                <When is="CompulsoryAnnuity">
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}].companyName`}
                                            label="Company name"
                                            validate={composeValidators(
                                                requiredValidator,
                                                maximumLength(60)
                                            )}
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={'bankingDetails'}
                                        property={'companyName'}
                                    />
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            label="Company registration number"
                                            name={`bankingDetails[${index}].accountHoldersIdNumber`}
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={'bankingDetails'}
                                        property={'accountHoldersIdNumber'}
                                    />
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}]companyReference`}
                                            label="Reference number"
                                            validate={composeValidators(
                                                requiredValidator,
                                                maximumLength(60)
                                            )}
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={'bankingDetails'}
                                        property={'ReferenceNumber'}
                                    />
                                    <Grid item xs={6}>
                                        <VirtualizedSearchSelectField
                                            name={`bankingDetails[${index}].bankName`}
                                            validate={requiredValidator}
                                            options={bankNames}
                                            matchPos="start"
                                        />
                                    </Grid>

                                    <ResetStatus prefix={`bankingDetails`} property={'bankName'} />
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}].accountNumber`}
                                            validate={requiredValidator}
                                            mask={NumberMask}
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'accountNumber'}
                                    />
                                    <Field
                                        name={`bankingDetails[${index}].bankName`}
                                        subscription={{ value: true }}
                                        render={({ input: { value: bankName } }) => {
                                            const [
                                                localBranchCodes,
                                                setLocalBranchCodes,
                                            ] = useState<UniversalBranch[]>([]);
                                            const [loading, setLoading] = useState(false);

                                            useEffect(() => {
                                                if (!bankName) {
                                                    setLocalBranchCodes([]);
                                                    return;
                                                }

                                                const universalBranch = universalBranches.find(
                                                    uc => uc.bankName === bankName
                                                );

                                                if (universalBranch) {
                                                    setLocalBranchCodes([universalBranch]);
                                                } else {
                                                    setLoading(true);
                                                    loadBankBranches(bankName)
                                                        .then(result => setLocalBranchCodes(result))
                                                        .finally(() => setLoading(false));
                                                }
                                            }, [bankName]);

                                            const universalBranch = universalBranches.find(
                                                uc => uc.bankName === bankName
                                            );

                                            return (
                                                <Grid item xs={6}>
                                                    <VirtualizedSearchSelectField
                                                        name={`bankingDetails[${index}].branchCode`}
                                                        options={localBranchCodes}
                                                        label="Branch Name"
                                                        validate={requiredValidator}
                                                        disabled={
                                                            !!universalBranch &&
                                                            localBranchCodes.length === 1
                                                        }
                                                        loading={loading}
                                                        matchPos="start"
                                                    />
                                                </Grid>
                                            );
                                        }}
                                    />
                                    <WhenFieldChanges
                                        field={`bankingDetails[${index}].bankName`}
                                        set={`bankingDetails[${index}].branchCode`}
                                        to={(bankName: string) => {
                                            const universalBranch = universalBranches.find(
                                                uc => uc.bankName === bankName
                                            );
                                            if (universalBranch) {
                                                return universalBranch.value;
                                            }
                                            setLoadBranches(true);
                                            return null;
                                        }}
                                    />
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}].branchCode`}
                                            validate={requiredValidator}
                                            mask={NumberMask}
                                            disabled
                                        />
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'branchCode'}
                                    />
                                    <Grid item xs={6}>
                                        <FullWidthTextField
                                            name={`bankingDetails[${index}].specialInstructions`}
                                            label="Special Instructions"
                                            multiline
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FullWidthRadioGroup
                                            name={`bankingDetails[${index}].accountType`}
                                            validate={requiredValidator}
                                        >
                                            <RadioOption value="Current" />
                                            <RadioOption value="Savings" />
                                            <RadioOption value="Transmission" />
                                        </FullWidthRadioGroup>
                                    </Grid>
                                    <ResetStatus
                                        prefix={`bankingDetails`}
                                        property={'accountType'}
                                    />

                                    <React.Fragment>
                                        <Grid item xs={6}>
                                            <FullWidthRadioGroup
                                                name={`bankingDetails[${index}].accountHolderRelationship`}
                                                validate={requiredValidator}
                                            >
                                                <RadioOption value="Own" />
                                                <RadioOption value="Joint" />
                                                <RadioOption value="Other" />
                                            </FullWidthRadioGroup>
                                        </Grid>
                                        <WhenFieldChanges
                                            field={`bankingDetails[${index}].accountHolderRelationship`}
                                            condition={value => value !== 'Other'}
                                            set={`bankingDetails[${index}].accountHolderRelationshipDescription`}
                                            to={null}
                                        />
                                        <Condition
                                            when={`bankingDetails[${index}].accountHolderRelationship`}
                                            is="Other"
                                        >
                                            <True>
                                                <Grid item xs={6}>
                                                    <FullWidthTextField
                                                        name={`bankingDetails[${index}].accountHolderRelationshipDescription`}
                                                        validate={requiredValidator}
                                                    />
                                                </Grid>
                                            </True>
                                        </Condition>
                                    </React.Fragment>

                                    <Grid item xs={6}>
                                        <FullWidthRadioGroup
                                            name={`bankingDetails[${index}].bankAccountStatus`}
                                        >
                                            <RadioOption value="Verified" disabled />
                                            <RadioOption value="Not verified" disabled />
                                        </FullWidthRadioGroup>
                                    </Grid>
                                </When>
                            </Condition>
                        </Grid>
                    </React.Fragment>
                );
            },
        });
    }
);
interface ResetProps {
    prefix: string;
    property: string;
}

const ResetStatus = ({ property, prefix }: ResetProps) => {
    return (
        <React.Fragment>
            <WhenFieldChanges
                field={`${prefix}${property}`}
                set={`${prefix}bankAccountStatus`}
                to="Not verified"
            />
            <WhenFieldChanges
                field={`${prefix}${property}`}
                set={`${prefix}bankResponse`}
                to={null}
            />
        </React.Fragment>
    );
};
export const MultipleBankingDetailsView = withStyles(styles)(BankingDetailsViewComponent);
